<template>
    <v-data-table
        v-if="items"
        fixed-header
        :headers="currentReportHeaders"
        :items="items"
        :loading="loading"
        :items-per-page="10"
        :hide-default-footer="false"
        :footer-props="{
            'items-per-page-options': [15, 30, 50, -1],
            'items-per-page-text': t('datatable::pagination.items.per.page.text'),
        }"
        @update:options="setItemColors"
    >
        <template #[`item.patient.dms_id`]="{ item }">
            <div
                v-if="item.patient?.dms_id && can('patients.preview')"
                class="link"
                @click="$root.goTo(patientsItemUrl(item.patient))"
            >
                {{ item.patient?.dms_id ?? t('patients::label.deleted') }}
            </div>
            <div v-else>
                {{ item.patient?.dms_id ?? t('patients::label.deleted') }}
            </div>
        </template>
        <template #[`item.day_1.date_taken`]="{ item }">
            <div v-if="item.day_1" :class="`blood-status ${item.day_1.color_result}--text`">
                <div :class="`status ${item.day_1.color_result}--text`">
                    <v-icon>icon icon-blood-result</v-icon>
                </div>
                <span
                    v-text="
                        item.day_1.date_taken
                            ? moment(item.day_1.date_taken).format($root.dateFormat)
                            : t('default::label.na')
                    "
                ></span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.day_2.date_taken`]="{ item }">
            <div v-if="item.day_2" :class="`blood-status ${item.day_2.color_result}--text`">
                <div :class="`status ${item.day_2.color_result}--text`">
                    <v-icon>icon icon-blood-result</v-icon>
                </div>
                <span
                    v-text="
                        item.day_2.date_taken
                            ? moment(item.day_2.date_taken).format($root.dateFormat)
                            : t('default::label.na')
                    "
                ></span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.day_3.date_taken`]="{ item }">
            <div v-if="item.day_3" :class="`blood-status ${item.day_3.color_result}--text`">
                <div :class="`status ${item.day_3.color_result}--text`">
                    <v-icon>icon icon-blood-result</v-icon>
                </div>
                <span
                    v-text="
                        item.day_3.date_taken
                            ? moment(item.day_3.date_taken).format($root.dateFormat)
                            : t('default::label.na')
                    "
                ></span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.day_4.date_taken`]="{ item }">
            <div v-if="item.day_4" :class="`blood-status ${item.day_4.color_result}--text`">
                <div :class="`status ${item.day_4.color_result}--text`">
                    <v-icon>icon icon-blood-result</v-icon>
                </div>
                <span
                    v-text="
                        item.day_4.date_taken
                            ? moment(item.day_4.date_taken).format($root.dateFormat)
                            : t('default::label.na')
                    "
                ></span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.day_5.date_taken`]="{ item }">
            <div v-if="item.day_5" :class="`blood-status ${item.day_5.color_result}--text`">
                <div :class="`status ${item.day_5.color_result}--text`">
                    <v-icon>icon icon-blood-result</v-icon>
                </div>
                <span
                    v-text="
                        item.day_5.date_taken
                            ? moment(item.day_5.date_taken).format($root.dateFormat)
                            : t('default::label.na')
                    "
                ></span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.confirmed`]="{ item }">
            <div :class="`grid-status item-confirmed ${item.confirmed}`">
                <span>
                    {{ item.confirmed ? t('default::label.confirmed') : t('default::label.unconfirmed') }}
                </span>
            </div>
        </template>
        <template #[`item.patient_status`]="{ item }">
            <div v-if="item.patient_status" class="grid-status">
                <span>
                    {{ t(`patients::status.${item.patient_status}`) }}
                </span>
            </div>
            <div v-else>
                <span>{{ t('default::label.na') }}</span>
            </div>
        </template>
        <template #[`item.red_recovery_email_sent`]="{ item }">
            <v-edit-dialog :ref="`editDialogRedRecovery${item.id}`" @save="saveItemRow(item)">
                <v-icon left>mdi-pencil</v-icon
                ><span class="grid-editable"> {{ item.red_recovery_email_sent || t('default::label.na') }} </span>
                <template #input>
                    <div class="form-minutes">
                        <div class="input-textarea">
                            <v-textarea
                                :ref="`editDialogInputRedRecovery${item.id}`"
                                v-model="item.red_recovery_email_sent"
                                :label="t('reports::trackers.grid.label.red_recovery_email_sent')"
                                dense
                                outlined
                                rounded
                            ></v-textarea>
                        </div>
                        <div class="input-actions pt-4">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="fakeSaveItemRowRedRecovery(item)"
                                    >
                                        <v-icon>mdi-check</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.save') }}</span>
                            </v-tooltip>
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="closeDialog(item)"
                                    >
                                        <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.close') }}</span>
                            </v-tooltip>
                        </div>
                    </div>
                </template>
            </v-edit-dialog>
        </template>

        <template #[`item.cnrd_date`]="{ item }">
            <v-edit-dialog :ref="`editDialogCnrdDate${item.id}`" @save="saveItemRow(item)">
                <v-icon left>mdi-pencil</v-icon
                ><span class="grid-editable"> {{ item.cnrd_date || t('default::label.na') }} </span>
                <template #input>
                    <div class="form-minutes">
                        <div class="input-textarea">
                            <v-menu transition="scale-transition" offset-y min-width="290px">
                                <template #activator="{ on, attrs }">
                                    <v-text-field
                                        v-model="item.cnrd_date"
                                        readonly
                                        :label="t('reports::trackers.grid.label.cnrd_date')"
                                        dense
                                        outlined
                                        rounded
                                        v-bind="attrs"
                                        v-on="on"
                                    ></v-text-field>
                                </template>
                                <v-date-picker v-model="item.cnrd_date" width="290px"> </v-date-picker>
                            </v-menu>
                        </div>
                        <div class="input-actions pt-4">
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="primary"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="fakeSaveItemRowCnrdDate(item)"
                                    >
                                        <v-icon>mdi-check</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.save') }}</span>
                            </v-tooltip>
                            <v-tooltip top transition="v-fab-transition">
                                <template #activator="{ on, attrs }">
                                    <v-btn
                                        color="disabled-color"
                                        icon
                                        v-bind="attrs"
                                        v-on="on"
                                        @click="closeDialog(item)"
                                    >
                                        <v-icon>mdi-close</v-icon>
                                    </v-btn>
                                </template>
                                <span>{{ t('default::button.close') }}</span>
                            </v-tooltip>
                        </div>
                    </div>
                </template>
            </v-edit-dialog>
        </template>
    </v-data-table>
</template>

<script>
export default {
    name: 'AmberResultsDataTable',
    components: {},
    data() {
        return {
            hiddenColumns: ['id'],
        };
    },
    computed: {
        loading: {
            get() {
                return this.$store.state.trackers.loading;
            },
            set(loading) {
                return this.$store.dispatch('trackers/setLoading', loading);
            },
        },
        currentReportHeaders() {
            const columns = [
                {
                    text: t('reports::trackers.grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                // {
                //     text: t('reports::trackers.grid.label.completed_by'),
                //     value: 'completed_by',
                //     sortable: true,
                //     width: 200,
                // },
                {
                    text: t('reports::trackers.grid.label.patient_id'),
                    value: 'patient.dms_id',
                    fields: 'patient',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('reports::trackers.grid.label.day_1'),
                    value: 'day_1.date_taken',
                    fields: 'day_1',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.day_2'),
                    value: 'day_2.date_taken',
                    fields: 'day_2',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.day_3'),
                    value: 'day_3.date_taken',
                    fields: 'day_3',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.day_4'),
                    value: 'day_4.date_taken',
                    fields: 'day_4',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.day_5'),
                    value: 'day_5.date_taken',
                    fields: 'day_5',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.red_recovery_email_sent'),
                    value: 'red_recovery_email_sent',
                    sortable: true,
                    align: 'center',
                    width: 300,
                },
                {
                    text: t('reports::trackers.grid.label.confirmed'),
                    value: 'confirmed',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.cnrd_date'),
                    value: 'cnrd_date',
                    sortable: true,
                    width: 180,
                },
                {
                    text: t('reports::trackers.grid.label.patient_status'),
                    value: 'patient_status',
                    sortable: true,
                    width: 180,
                },
            ];
            return columns.filter((x) => !this.hiddenColumns.includes(x.value));
        },
        items() {
            return this.$store.state.trackers.currentReportItems;
        },
    },
    watch: {
        items() {
            _.delay(() => {
                this.setItemColors();
            }, 300);
        },
    },
    mounted() {
        this.setItemColors();
    },
    methods: {
        setItemColors() {
            if (!this.items) {
                return;
            }
            if (this.items && _.isEmpty(this.items)) {
                return;
            }
            _.delay(() => {
                const items = Array.from(this.$el.querySelector('tbody').childNodes);
                items.forEach((item) => {
                    const confirmed = item.querySelector('.item-confirmed').classList.contains('true');
                    item.classList.remove(...['colored', 'new', 'color-red']);
                    if (confirmed) {
                        item.classList.add(...['colored', 'new', 'color-red']);
                    }
                });
            }, 300);
        },
        closeDialog(item) {
            const cnrdDialog = this.$refs[`editDialogCnrdDate${item.id}`];
            const redRecoveryDialog = this.$refs[`editDialogRedRecovery${item.id}`];
            if (cnrdDialog) {
                cnrdDialog.$emit('close');
                cnrdDialog.isActive = false;
            }
            if (redRecoveryDialog) {
                redRecoveryDialog.$emit('close');
                redRecoveryDialog.isActive = false;
            }
        },
        fakeSaveItemRowCnrdDate(item) {
            this.$refs[`editDialogCnrdDate${item.id}`].save(item.comment);
            this.$refs[`editDialogCnrdDate${item.id}`].$emit('save');
        },
        fakeSaveItemRowRedRecovery(item) {
            this.$refs[`editDialogRedRecovery${item.id}`].save(item.reason);
            this.$refs[`editDialogRedRecovery${item.id}`].$emit('save');
        },
        saveItemRow(item) {
            const data = {
                data: {
                    id: item.id,
                    cnrd_date: item.cnrd_date,
                    red_recovery_email_sent: item.red_recovery_email_sent,
                },
            };
            axios
                .put(route('api.v1.trackers.red-results.update'), { data })
                .then(() => {
                    this.$root.notify(
                        'info',
                        t('default::notification.success'),
                        t('reports::trackers.mhbu.item.updated')
                    );
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                });
        },
        patientsItemUrl(item) {
            return route('patients.preview', { id: item.id });
        },
    },
};
</script>
